import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NormeSpaDualisticoFormValidators } from 'app/features/revisione53/core/forms/amm-controllo/norme-spa-dualistico/validators/norme-spa-dualistico.form-validators';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { NormeSpaDualisticoWarningsService } from './warnings/norme-spa-dualistico.warnings';
import {
    buildConsiglioGestioneFormArray,
    buildConsiglioSorveglianzaFormArray,
    buildLiquidatoriFormArray,
    buildOrganoControlloFormArray,
    buildRevisoreFormArray,
    clearNormeFormArrays,
} from '../common/helpers/norme-form-groups.helper';
import { normalizeArraysNorme } from '../common/helpers/norme-normalize.helper';
import { cloneDeep } from 'lodash-es';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { NormeSpaDualisticoFormListeners } from './listeners/norme-spa-dualistico.form-listeners';
import { BaseRevisioneFormService } from '../../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';
import { getControl } from '@shared/helpers/form.helper';
import { controlloCheckConsiglioSorveglianza } from '../common/helpers/norme-spa-helpers.helper';

@Injectable()
export class NormeSpaDualisticoFormService extends BaseRevisioneFormService<NormeSpaDualisticoWarningsService> {
    constructor(protected warningsService: NormeSpaDualisticoWarningsService) {
        super(warningsService);
    }

    override initListener(): void {
        new NormeSpaDualisticoFormListeners(this._form, this.store).setListeners();
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato pratica
            'my_IdAssegnazione',

            // Global fields
            'my_GlobalFields.my_StampaMinistero_ChkSRL',
            'my_GlobalFields.my_StampaMinistero_ChkSPO',
            'my_GlobalFields.my_StampaMinistero_ChkSPD',
            'my_GlobalFields.my_StampaMinistero_ChkSPM',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,

            // Bilancio
            `${formPaths.bilancio}.my_CkEnteControllaSocObbRevCont`,
            `${formPaths.bilancio}.my_CkRedazBilConsol`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,

            // Raggiungimento scopo sociale
            `${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`,
            `${formPaths.scopoSociale}.my_RicaviVendDep`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_SociFisCoopData`,
            `${formPaths.baseSociale}.my_SociGiuCoopData`,
            `${formPaths.baseSociale}.my_SociSpecData`,
            `${formPaths.baseSociale}.my_ULAPrimo`,
            `${formPaths.baseSociale}.my_ULASecondo`,

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,
        ]);
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_IdAssegnazione: '',
                my_GlobalFields: this.fb.group({
                    my_StampaMinistero_ChkSRL: '',
                    my_StampaMinistero_ChkSPO: '',
                    my_StampaMinistero_ChkSPD: '',
                    my_StampaMinistero_ChkSPM: '',
                }),
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_CkEnteControllaSocObbRevCont: '',
                        my_CkRedazBilConsol: '',
                    }),

                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_SociFisCoopData: '',
                        my_SociGiuCoopData: '',
                        my_SociSpecData: '',
                        my_ULAPrimo: '',
                        my_ULASecondo: '',
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_RicaviVendAnno1Dep: '',
                        my_RicaviVendDep: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno1: '',
                        my_BilAnno2: '',
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtTOT1: '',
                            my_StPaAtTOT2: '',
                        }),
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_CkNorme: '',
                        my_CalcObbOrganoContr: '',
                        my_CkLiquidatoriRelazionano: '',
                        my_CriteriRelazioneLiquidatori: '',
                        my_CkSindaciRiunioniLiquidatori: '',
                        my_CkRegistroDelibLiquidatori: '',
                        my_DataAssembleaLiquidazVolontaria: '',
                        my_ControlloRevLegali: '',
                        my_ControlloSoci: '',
                        my_ControlloRevisoreContabileSocio: '',
                        my_NoteAttivitaLiquidatori: '',
                        my_CkOrgSocietCorrCostit: '',
                        my_CkOrgSocietCorrFunz: '',
                        my_NoteEdOsservazioni: '',
                        my_NumLiquidatori: '',

                        my_AllLiquidatori: this.fb.group({
                            my_AllElemLiquidatori: this.fb.array([]),
                        }),

                        my_NormeSrl: this.fb.group({
                            my_lblSrlContrContab: '',
                        }),

                        my_NormeSpaDualistico: this.fb.group({
                            my_lblContrCont: '',
                            my_LabelConsiglioSorveglianza: '',
                            my_LabelConsiglioGestione: '',
                            my_CkSpdCoop9soci: '',
                            my_CkSpdAssembNominSorv: '',
                            my_CkSpdAssembDetCompSorv: '',
                            my_CkSpdAssembDelibDistribUtil: '',
                            my_SpdNoteComposAssemb: '',

                            my_SpdConsiglioSorveglianza: this.fb.group({
                                my_SpdElemConsiglioSorveglianza: this.fb.array([]),
                            }),

                            my_CkSpdMaggSorvSoci: '',
                            my_CkSpdCompElettiStrFinan: '',
                            my_CkSpdCompElettiLimite: '',
                            my_CkSpdNominaConsGest: '',
                            my_CkSpdApprovaBilEserc: '',
                            my_CkSpdRelazCaratMut: '',
                            my_CkSpdVigila: '',
                            my_CkSpdRiferisceAssemb: '',
                            my_SpdNoteComposConsSorv: '',

                            my_gruppo5: this.fb.group({
                                my_CkSPDMembroRevisore: '',
                            }),

                            my_SpdConsiglioGestione: this.fb.group({
                                my_SpdElemConsiglioGestione: this.fb.array([]),
                            }),

                            my_SpdNoteComposConsGest: '',
                            my_SpdScadenzaAmministratori: '',
                            my_CkSpdAffidamContrCont: '',
                            my_SpdNoteComposContrContab: '',
                        }),

                        my_AllRevisoriControlloContabile: this.fb.group({
                            my_AllElemRevisoriControlloContabile: this.fb.array([]),
                        }),

                        my_AllCollegioSindacale: this.fb.group({
                            my_AllElemCollegioSindacale: this.fb.array([]),
                        }),

                        my_SocRevisioneControlloContabile: this.fb.group({
                            my_Id_SocPrecaricata: '',
                            my_RagSocSocRevisione: '',
                            my_DataAffIncarico: '',
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: NormeSpaDualisticoFormValidators,
            }
        );
    }

    protected override beforePopulateForm(myVerbale: MyVerbale): void {
        const v = normalizeArraysNorme(cloneDeep(myVerbale));
        this.patchAll(v);
    }

    private lastCheckVerbaleId: string | null = null;
    private lastCheckMemberCount: number | null = null;

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        // REM-42: il VBS ricalcolava i contatori my_ControlloRevLegali/my_ControlloSoci dell'organo
        // di controllo (per SPD il Consiglio di Sorveglianza) in XDocument_OnLoad. I listener NCR
        // reagiscono solo al cambio membri, quindi il primo calcolo va forzato al load. Si ripete anche
        // a parità di verbale se nel frattempo sono arrivati i membri (caricamento asincrono): senza il
        // controllo sul conteggio, un primo giro con array ancora vuoto blocca il ricalcolo per il resto
        // della sessione.
        const idAssegnazione = myVerbale?.my_IdAssegnazione ?? null;
        if (myVerbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme !== CkNormeValuesEnum.normeSpaDualistico) return;
        const componentiConsiglioSorveglianza = getControl(this._form, `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza`)?.value ?? [];
        const memberCount = componentiConsiglioSorveglianza.length;
        if (this.lastCheckVerbaleId === idAssegnazione && this.lastCheckMemberCount === memberCount) return;
        this.lastCheckVerbaleId = idAssegnazione;
        this.lastCheckMemberCount = memberCount;
        controlloCheckConsiglioSorveglianza(this._form, this.store, componentiConsiglioSorveglianza);
    }

    private patchAll(my_Verbale: MyVerbale): void {
        if (my_Verbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme === CkNormeValuesEnum.normeSpaDualistico) {
            buildConsiglioGestioneFormArray(this.fb, my_Verbale, this._form);
            buildLiquidatoriFormArray(this.fb, my_Verbale, this._form);
            buildConsiglioSorveglianzaFormArray(this.fb, my_Verbale, this._form);
            buildRevisoreFormArray(this.fb, my_Verbale, this._form);
            buildOrganoControlloFormArray(this.fb, my_Verbale, this._form);
        } else {
            // Verbale di norma diversa: svuota gli array per evitare residui dal verbale precedente.
            clearNormeFormArrays(this._form);
        }
    }
}
